import React, { useState } from 'react';

import { Button } from '@/design-system/button';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../api/warehouse.api';
import { FULFILLMENT_STATUS, carrierLabel } from '../status';
import type {
  ManualFulfillmentStatus,
  OutboundOrder,
  OutboundOrderFulfillment,
} from '../types';
import { ActionDialog } from './dialogs';
import { FormField, TextArea } from './form';
import { codeCls, KV, MappedBadge, Section } from './ui';

/** Which manual statuses an order may move to from its current status. */
const NEXT_FOR: Partial<Record<string, ManualFulfillmentStatus[]>> = {
  SHIPPED: ['IN_DELIVERY', 'DELIVERED', 'FAILED'],
  IN_DELIVERY: ['DELIVERED', 'FAILED'],
};

const ACTION: Record<
  ManualFulfillmentStatus,
  { label: string; icon: string; tone: 'primary' | 'destructive' }
> = {
  IN_DELIVERY: {
    label: 'In delivery',
    icon: 'ri-truck-line',
    tone: 'primary',
  },
  DELIVERED: {
    label: 'Delivered',
    icon: 'ri-checkbox-circle-line',
    tone: 'primary',
  },
  FAILED: { label: 'Failed', icon: 'ri-close-circle-line', tone: 'destructive' },
};

/**
 * An order a third-party carrier (Lion Parcel) took: no Shipments row, so
 * ops set its progress here — In delivery → Delivered / Failed — with a
 * note for the timeline. The AWB was issued at Submit shipping (shipment
 * format). Terminal once Delivered/Failed.
 */
export const ManualFulfillmentSection: React.FC<{
  order: Pick<OutboundOrder, 'id' | 'code' | 'status'> & {
    fulfillment: OutboundOrderFulfillment;
  };
  onUpdated: (order: OutboundOrder) => void;
}> = ({ order, onUpdated }) => {
  const f = order.fulfillment;
  const [target, setTarget] = useState<ManualFulfillmentStatus | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const next = NEXT_FOR[order.status] ?? [];

  const open = (s: ManualFulfillmentStatus) => {
    setNote('');
    setTarget(s);
  };

  const confirm = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const updated = await warehouseApi.updateOutboundFulfillment(order.id, {
        status: target,
        note: note.trim() || null,
      });
      toast.success(`Status diupdate: ${ACTION[target].label}`, {
        description: order.code,
      });
      setTarget(null);
      onUpdated(updated);
    } catch (err) {
      toast.error('Update status gagal', {
        description: parseWarehouseError(err).message,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title={`Pengiriman ${carrierLabel(f.carrier) ?? 'manual'}`}>
      <div className="flex flex-col gap-3" data-testid="manual-fulfillment">
        <div>
          <KV
            label="Waybill"
            value={
              f.waybill ? <span className={codeCls}>{f.waybill}</span> : null
            }
          />
          <KV
            label="Status"
            value={
              f.status ? (
                <MappedBadge map={FULFILLMENT_STATUS} value={f.status} />
              ) : null
            }
          />
        </div>
        {next.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {next.map((s) => (
              <Button
                key={s}
                tone={ACTION[s].tone}
                style={ACTION[s].tone === 'primary' ? 'filled' : 'stroke'}
                size="sm"
                data-testid={`manual-status-${s}`}
                onClick={() => open(s)}
                leftIcon={<i className={`${ACTION[s].icon} text-[14px]`} />}
              >
                {ACTION[s].label}
              </Button>
            ))}
          </div>
        )}
      </div>
      <ActionDialog
        open={!!target}
        title={target ? `Tandai ${ACTION[target].label}` : ''}
        subtitle={order.code}
        description="Tidak bisa dikembalikan."
        confirmLabel={target ? ACTION[target].label : ''}
        tone={target === 'FAILED' ? 'destructive' : 'primary'}
        icon={target ? ACTION[target].icon : undefined}
        busy={busy}
        testId="manual-status-dialog"
        onClose={() => setTarget(null)}
        onConfirm={() => void confirm()}
      >
        <FormField
          label={target === 'FAILED' ? 'Alasan' : 'Catatan'}
          htmlFor="mf-note"
          optional
        >
          <TextArea
            id="mf-note"
            value={note}
            onChange={setNote}
          />
        </FormField>
      </ActionDialog>
    </Section>
  );
};
